import { ChangeEvent } from 'react';
import { Checkbox, Input, Table, Box } from '@nimbus-ds/components';
import { WeekdaysType } from '@tiendanube/common';
import useTranslationShipping from 'domains/Shipping/useTranslationShipping';
import BusinessHourActions from './BusinessHourActions';
import BusinessHourItemSkeleton from './Skeleton';
import TimeInputBlock from './TimeInputBlock';
import {
  END_DEFAULT,
  START_DEFAULT,
  ExtendedBusinessHoursResponseDto,
} from '../utils';

interface BusinessHourItemProps {
  day: WeekdaysType;
  isDayChanged: boolean;
  businessHours: ExtendedBusinessHoursResponseDto[];
  errors: Partial<Record<string, string>>;
  changeHour: (value: ExtendedBusinessHoursResponseDto) => void;
  handleApplyAll: (newBusinessHour: ExtendedBusinessHoursResponseDto) => void;
  handleChangeDay: (day: WeekdaysType, active: boolean) => void;
  handleAddBusinessHour: (day: WeekdaysType) => void;
  handleRemoveBusinessHour: (day: WeekdaysType) => void;
  isActive: boolean;
}

function BusinessHourItem({
  day,
  isDayChanged,
  businessHours,
  errors,
  changeHour,
  handleApplyAll,
  handleChangeDay,
  handleAddBusinessHour,
  handleRemoveBusinessHour,
  isActive,
}: Readonly<BusinessHourItemProps>): JSX.Element {
  const t = useTranslationShipping(
    'deliveryMethods.pickupPoints.form.secondStep.businessHours',
  );

  const handleChange = (e: ChangeEvent<HTMLInputElement>, index: number) => {
    const currentBusinessHour = businessHours[index];
    const newBusinessHour: ExtendedBusinessHoursResponseDto & {
      index?: number;
    } = {
      ...currentBusinessHour,
      day: day,
      start:
        e.target.name === 'start'
          ? e.target.value
          : currentBusinessHour?.start ?? START_DEFAULT,
      end:
        e.target.name === 'end'
          ? e.target.value
          : currentBusinessHour?.end ?? END_DEFAULT,
    };

    changeHour(newBusinessHour);
  };

  return (
    <Table.Row backgroundColor="neutral-background">
      <Table.Cell padding="base">
        <Checkbox
          name={day}
          label={t(day)}
          checked={isActive}
          onChange={(e) => handleChangeDay(day, e.target.checked)}
        />
      </Table.Cell>
      <Table.Cell>
        {isActive ? (
          <Box display="flex" flexDirection="column" gap="1">
            {businessHours.map((currentBusinessHour, index) => (
              <Box
                key={`${day}-${index}`}
                minHeight={
                  errors[`${day}.${currentBusinessHour.index}.start`] ||
                  errors[`${day}.${currentBusinessHour.index}.end`]
                    ? '3.5rem'
                    : '2.5rem'
                }
              >
                <TimeInputBlock
                  day={day}
                  index={currentBusinessHour.index}
                  dayIndex={index}
                  type="start"
                  value={currentBusinessHour.start}
                  errors={errors}
                  onChange={handleChange}
                />
              </Box>
            ))}
          </Box>
        ) : (
          <Input
            type="text"
            name="start"
            disabled
            appearance="neutral"
            value={t('closed')}
            aria-label={`${t(day)} closed`}
          />
        )}
      </Table.Cell>
      <Table.Cell>
        {isActive ? (
          <Box display="flex" flexDirection="column" gap="1">
            {businessHours.map((currentBusinessHour, index) => (
              <Box
                key={`${day}-${index}`}
                minHeight={
                  errors[`${day}.${currentBusinessHour.index}.start`] ||
                  errors[`${day}.${currentBusinessHour.index}.end`]
                    ? '3.5rem'
                    : '2.5rem'
                }
              >
                <TimeInputBlock
                  day={day}
                  index={currentBusinessHour.index}
                  dayIndex={index}
                  type="end"
                  value={currentBusinessHour.end}
                  errors={errors}
                  onChange={handleChange}
                />
              </Box>
            ))}
          </Box>
        ) : (
          <Input
            type="text"
            name="end"
            disabled
            appearance="neutral"
            value={t('closed')}
            aria-label={`${t(day)} closed`}
          />
        )}
      </Table.Cell>
      <Table.Cell padding="small">
        {isActive && (
          <Box display="flex" flexDirection="column" gap="2">
            {businessHours.map((currentBusinessHour, index) => (
              <Box
                key={`${day}-${index}`}
                height="2.2rem"
                display="flex"
                alignItems="center"
                role="row"
                aria-label={`Business hour ${index + 1} actions`}
              >
                {index === businessHours.length - 1 && (
                  <BusinessHourActions
                    day={day}
                    isDayChanged={isDayChanged}
                    businessHours={businessHours}
                    handleApplyAll={handleApplyAll}
                    handleAddBusinessHour={handleAddBusinessHour}
                    handleRemoveBusinessHour={handleRemoveBusinessHour}
                  />
                )}
              </Box>
            ))}
          </Box>
        )}
      </Table.Cell>
    </Table.Row>
  );
}
BusinessHourItem.Skeleton = BusinessHourItemSkeleton;
export default BusinessHourItem;
